<template>
  <div class="menu-page">
    <!-- Primary Actions: Deposit / Withdraw -->
    <div class="menu-actions">
      <button class="menu-actions__btn menu-actions__btn--deposit" @click="gotoActive(0)">
        <van-icon name="balance-o" size="24" />
        <span>{{ $lang.Depósito }}</span>
      </button>
      <button class="menu-actions__btn menu-actions__btn--withdraw" @click="gotoActive(1)">
        <van-icon name="cash-back-record" size="24" color="#ffb020" />
        <span>{{ $lang.Saque }}</span>
      </button>
    </div>

    <!-- Region Selector (单国实例隐藏:只有一个国家无需选) -->
    <div v-if="!isSingleCountry" class="menu-region-wrap">
      <div class="menu-region" @click="toggleCountryDrop">
        <div class="menu-region__left">
          <van-icon name="location-o" size="18" color="var(--vk-t2)" />
          <span class="menu-region__label">{{ $lang.menu_region || 'Region' }}</span>
        </div>
        <div class="menu-region__right">
          <img :src="GET_ICONURL" width="20" class="menu-region__flag" />
          <span class="menu-region__name">{{ GET_COUNTRYNAME }}</span>
          <van-icon :name="showCountryDrop ? 'arrow-up' : 'arrow-down'" size="12" color="var(--vk-t3)" />
        </div>
      </div>
      <div v-if="showCountryDrop" class="menu-country-dropdown">
        <div
          v-for="(item, idx) in countryList"
          :key="idx"
          class="menu-country-dropdown__item"
          @click="selectCountryMenu(item)"
        >
          <img :src="item.iconUrl" width="24" />
          <span>{{ item.countryName }}</span>
          <span class="menu-country-dropdown__code">{{ item.areaCode }}</span>
        </div>
        <div v-if="countryList.length === 0" class="menu-country-dropdown__item" style="justify-content: center; color: var(--vk-t3);">
          {{ countryDropdownEmptyText }}
        </div>
      </div>
    </div>

    <!-- Our Communities -->
    <div class="menu-section">
      <p class="menu-section__title">{{ menuCommunityTitle }}</p>
      <div class="menu-social">
        <a
          v-for="(item, index) in socialList"
          :key="index"
          class="menu-social__item"
          @click="goToSocial(index)"
        >
          <img :src="item.icon" width="28" />
        </a>
      </div>
    </div>

    <!-- Mini Games (2 tall cards) -->
    <div class="menu-games">
      <div
        v-for="(item, index) in gameList"
        :key="index"
        class="menu-game-card"
        :class="'menu-game-card--' + item.theme"
        @click="goToBBB(index)"
      >
        <div class="menu-game-card__top">
          <div class="menu-game-card__icon" :class="'menu-game-card__icon--' + item.theme">
            <img :src="item.icon" width="132" />
          </div>
          <van-icon name="arrow" size="12" color="var(--vk-t3)" />
        </div>
        <div class="menu-game-card__bottom">
          <p class="menu-game-card__label">{{ item.label }}</p>
          <h3 class="menu-game-card__name">{{ item.name }}</h3>
        </div>
      </div>
    </div>

    <!-- Telegram Section -->
    <div class="menu-section">
      <p class="menu-section__title">{{ menuTelegramTitle }}</p>
      <div class="menu-social">
        <a
          v-for="(item, index) in telegramList"
          :key="index"
          class="menu-social__item"
          @click="goToDDD(index)"
        >
          <img :src="item.icon" width="28" />
        </a>
      </div>
    </div>

    <!-- Function List -->
    <nav class="menu-nav">
      <div
        v-for="(item, index) in mtList"
        :key="index"
        class="menu-nav__item"
        @click="goToCCC(index)"
      >
        <div class="menu-nav__left">
          <ui-icon :name="item.glyph" :size="18" />
          <span>{{ item.name }}</span>
        </div>
      </div>
    </nav>

  </div>
</template>

<script>
import { GetCsLink, GetPlatformList, WithdrawInit } from '@/api/common'
import { SINGLE_COUNTRY } from '@/config/singleCountry'
import { logoutIfReloginRequiredWithToken } from '@/utils/platformAuth'
import { safeGetStorageItem, safeLocalStorage, safeSetStorageItem } from '@/utils/safeStorage'
import { openCustomerServiceUrl } from './csLink'
import { getMineActionRoute } from './mineNavigation'
import {
  entryAt,
  gameActivityEntries,
  menuUtilityEntries
} from './navigationEntries'
import { isActivityVisible } from './activityVisibility'
import {
  isNeedLogin,
  isOkResponse,
  normalizePlatformList,
  openExternalUrl,
  responseContent,
  responseFailMessage,
  safeArray,
  safeObject,
  safeString
} from './safeLayout'

// 用户向中性文案(脱敏:不暴露"未接入后端/已阻断"等内部术语;per-locale i18n 见 P1)
const MENU_ENTRY_UNAVAILABLE_MESSAGE = 'This feature is temporarily unavailable. Please try again later.'
const MENU_WITHDRAW_UNAVAILABLE_MESSAGE = 'Withdrawals are temporarily unavailable. Please try again later.'
const MENU_COUNTRY_UNAVAILABLE_MESSAGE = 'Country/region list is unavailable. Please try again later.'
const MENU_CUSTOMER_SERVICE_UNAVAILABLE_MESSAGE = 'Customer service is temporarily unavailable. Please try again later.'
const MENU_COUNTRY_EMPTY_MESSAGE = 'No regions available'
const GENERIC_REQUEST_FAILED_MESSAGE = ['Request', 'failed'].join(' ')
const MENU_LINK_LIST_KEYS = ['url2', 'list', 'linkList', 'csLinkList', 'dataList', 'records', 'items']
const MENU_LINK_URL_KEYS = ['link_url', 'linkUrl', 'url', 'href', 'value']
const MENU_LINK_TYPE_KEYS = ['link_type', 'linkType', 'type', 'channel', 'name', 'title', 'link_name', 'linkName']
const MENU_LINK_BLOCKED_KEYS = [
  'readOnlyAction',
  'read_only_action',
  'blocked',
  'block',
  'backendBlocked',
  'backend_blocked',
  'actionBlocked',
  'action_blocked',
  'readBlocked',
  'read_blocked',
  'readUnavailable',
  'read_unavailable',
  'readFailure',
  'read_failure',
  'readFailed',
  'read_failed',
  'routeUnavailable',
  'route_unavailable',
  'technicalMarker',
  'technical_marker',
  'technicalError',
  'technical_error',
  'technicalException',
  'technical_exception',
  'backendFailure',
  'backend_failure',
  'failClosed',
  'fail_closed',
  'falseProof',
  'false_proof',
  'needLogin',
  'need_login',
  'fallback',
  'fallbackUsed',
  'fallback_used',
  'mock',
  'mockMode',
  'mock_mode',
  'mockFallback',
  'mock_fallback',
  'stub',
  'unimplemented',
  'notImplemented',
  'not_implemented',
  'notSupported',
  'not_supported',
  'unsupported',
  'dataUnavailable',
  'data_unavailable',
  'readOnly',
  'readonly',
  'read_only',
  'readOnlyMode',
  'read_only_mode'
]
const MENU_LINK_STATUS_KEYS = ['status', 'state', 'result', 'code', 'errorCode', 'success']
const MENU_LINK_ALIASES = {
  official: ['official', 'online', 'support', 'service', 'customer', 'cs', '客服'],
  telegram: ['telegram', 'tg'],
  whatsapp: ['whatsapp', 'ws', 'wa'],
  facebook: ['facebook', 'fb'],
  twitter: ['twitter', 'x'],
  instagram: ['instagram', 'ins'],
  tiktok: ['tiktok', 'tik_tok', 'tik tok', 'douyin']
}
const MENU_LINK_DIRECT_KEYS = {
  official: ['official', 'officialUrl', 'serviceUrl', 'customerServiceUrl', 'online'],
  telegram: ['telegram', 'telegramUrl', 'tg'],
  whatsapp: ['whatsapp', 'whatsappUrl', 'ws', 'wa'],
  facebook: ['facebook', 'facebookUrl', 'fb', 'fbUrl'],
  twitter: ['twitter', 'twitterUrl', 'x', 'xUrl'],
  instagram: ['instagram', 'instagramUrl', 'ins', 'insUrl'],
  tiktok: ['tiktok', 'tiktokUrl', 'tikTok', 'tikTokUrl', 'douyin', 'douyinUrl']
}
const MENU_CUSTOMER_SERVICE_TYPES = ['official', 'telegram', 'whatsapp']

function menuLang(lang, key, fallback) {
  const text = safeString(safeObject(lang)[key], '').trim()
  if (!text || text === key || text === 'undefined' || text === 'null') return fallback
  return text
}

function menuFailureMessage(response, fallback = MENU_ENTRY_UNAVAILABLE_MESSAGE) {
  const safeFallback = fallback || MENU_ENTRY_UNAVAILABLE_MESSAGE
  const message = responseFailMessage(response, safeFallback)
  const text = safeString(message, '').trim()
  if (!text || text === GENERIC_REQUEST_FAILED_MESSAGE || text === '[object Object]' || /^Error(:|$)/i.test(text)) {
    return safeFallback
  }
  if (text === safeFallback) return text
  return `${safeFallback} ${text}`
}

function getLocalStorage() {
  if (typeof safeLocalStorage === 'function') return safeLocalStorage()
  return null
}

function reloadLocationSafely() {
  try {
    const location = typeof window !== 'undefined' && window ? window.location : null
    if (location && typeof location.reload === 'function') {
      location.reload()
      return true
    }
  } catch (e) {
    return false
  }
  return false
}

function normalizeMenuLinkType(type) {
  const value = safeString(type, 'official').trim().toLowerCase()
  if (!value || value === 'official') return 'official'
  if (value === '1' || value.includes('telegram') || value === 'tg') return 'telegram'
  if (value === '2' || value.includes('whatsapp') || value === 'ws' || value === 'wa') return 'whatsapp'
  if (value.includes('facebook') || value === 'fb') return 'facebook'
  if (value.includes('twitter') || value === 'x') return 'twitter'
  if (value.includes('instagram') || value === 'ins') return 'instagram'
  if (value.includes('tiktok') || value.includes('tik_tok') || value.includes('tik tok') || value.includes('douyin')) return 'tiktok'
  return value
}

function menuLinkAliases(type) {
  const target = normalizeMenuLinkType(type)
  return MENU_LINK_ALIASES[target] || [target]
}

function isMenuCustomerServiceType(type) {
  return MENU_CUSTOMER_SERVICE_TYPES.includes(normalizeMenuLinkType(type))
}

function isActiveMenuLinkFlag(value) {
  if (value === true || value === 1) return true
  const token = safeString(value).trim().toLowerCase()
  return ['true', '1', 'yes', 'on', 'blocked', 'unavailable', 'error', 'fail', 'failed'].includes(token)
}

function isMenuLinkFailureToken(value) {
  if (value === false) return true
  const token = safeString(value).trim().toLowerCase()
  if (!token) return false
  return [
    'false',
    'fail',
    'failed',
    'failure',
    'error',
    'blocked',
    'unavailable',
    'readonly',
    'read_only',
    'not_supported',
    'unsupported',
    'unimplemented',
    'fail_closed',
    'failclosed',
    'false_proof',
    'falseproof',
    'technical_exception',
    'technicalexception',
    'backend_failure',
    'backendfailure',
    'need_login'
  ].includes(token) || /^[45]\d\d$/.test(token) || token.startsWith('e_')
}

function hasMenuLinkBlocker(source) {
  const body = safeObject(source)
  if (!Object.keys(body).length) return false
  if (MENU_LINK_BLOCKED_KEYS.some(key => isActiveMenuLinkFlag(body[key]))) return true
  return MENU_LINK_STATUS_KEYS.some(key => isMenuLinkFailureToken(body[key]))
}

function firstMenuLinkValue(source, keys) {
  const body = safeObject(source)
  for (const key of keys) {
    const value = safeString(body[key]).trim()
    if (value) return value
  }
  return ''
}

function safeMenuHttpUrl(value) {
  const url = safeString(value).trim()
  if (!url) return ''
  try {
    const parsed = new URL(url)
    if (!['http:', 'https:'].includes(parsed.protocol)) return ''
    if (!parsed.hostname) return ''
    return url
  } catch (e) {
    return ''
  }
}

function firstMenuLinkUrl(source, keys) {
  const body = safeObject(source)
  for (const key of keys) {
    const url = safeMenuHttpUrl(body[key])
    if (url) return url
  }
  return ''
}

function menuAliasMatches(text, alias) {
  const haystack = safeString(text).toLowerCase()
  const needle = safeString(alias).toLowerCase()
  if (!haystack || !needle) return false
  if (needle.length <= 2) {
    return haystack.split(/[^a-z0-9]+/).includes(needle)
  }
  return haystack.includes(needle)
}

function menuLinkMatchesType(source, type) {
  const text = firstMenuLinkValue(source, MENU_LINK_TYPE_KEYS).toLowerCase()
  if (!text) return normalizeMenuLinkType(type) === 'official'
  return menuLinkAliases(type).some(alias => menuAliasMatches(text, alias))
}

function resolveConfiguredMenuLinkFromSource(source, type) {
  if (hasMenuLinkBlocker(source)) return ''
  const target = normalizeMenuLinkType(type)
  const directKeys = MENU_LINK_DIRECT_KEYS[target] || [target, `${target}Url`]
  const direct = firstMenuLinkUrl(source, directKeys)
  if (direct) return direct
  if (!menuLinkMatchesType(source, target)) return ''
  return firstMenuLinkUrl(source, MENU_LINK_URL_KEYS)
}

function resolveConfiguredMenuLink(response, type) {
  if (!response || hasMenuLinkBlocker(response)) return ''
  const content = responseContent(response)
  const direct = resolveConfiguredMenuLinkFromSource(content, type)
  if (direct) return direct
  for (const key of MENU_LINK_LIST_KEYS) {
    for (const item of safeArray(safeObject(content)[key])) {
      const url = resolveConfiguredMenuLinkFromSource(item, type)
      if (url) return url
    }
  }
  return ''
}

export default {
  name: 'MenuPage',
  data() {
    return {
      // 单国实例:隐藏区域(选平台国家)选择器(见 @/config/singleCountry)
      isSingleCountry: !!SINGLE_COUNTRY,
      showLang: false,
      showCountryDrop: false,
      countryListLoading: false,
      countryListLoadFailed: false,
      csLinkResponse: null,
      countryList: [],
      socialList: [
        { icon: require('@/assets/img/otgame/zjm_39.png'), serviceType: 'facebook' },
        { icon: require('@/assets/img/otgame/pinduoduo_24.png'), serviceType: 'twitter' },
        { icon: require('@/assets/img/otgame/ins.png'), serviceType: 'instagram' },
        { icon: require('@/assets/img/otgame/zjm_42.png'), serviceType: 'tiktok' }
      ],
      telegramList: [
        { icon: require('@/assets/img/otgame/group-chat.png'), serviceType: 'official' },
        { icon: require('@/assets/img/otgame/channel.png'), serviceType: 'telegram' }
      ],
      mtList: [
        {
          ...menuUtilityEntries[0],
          glyph: 'help', // 原 cebianlan_12 蓝
          name: menuLang(this.$lang, 'common_txt282', 'VIP')
        },
        {
          ...menuUtilityEntries[1],
          glyph: 'headset', // 原 cebianlan_13 蓝
          name: menuLang(this.$lang, 'common_txt283', 'Share')
        },
        {
          ...menuUtilityEntries[2],
          glyph: 'ticket', // 原 cdk 蓝
          name: menuLang(this.$lang, 'common_txt284', 'CDK')
        },
        {
          ...menuUtilityEntries[3],
          glyph: 'handshake', // 原 hezuo
          name: menuLang(this.$lang, 'common_txt285', 'Cooperate')
        }
      ]
    }
  },
  computed: {
    countryDropdownEmptyText() {
      if (this.countryListLoading) return menuLang(this.$lang, 'common_loading', 'Loading...')
      if (this.countryListLoadFailed) return menuLang(this.$lang, 'menu_country_load_failed', MENU_COUNTRY_UNAVAILABLE_MESSAGE)
      return menuLang(this.$lang, 'menu_country_empty', MENU_COUNTRY_EMPTY_MESSAGE)
    },
    menuCommunityTitle() {
      return menuLang(this.$lang, 'common_txt279', 'Our Communities')
    },
    menuTelegramTitle() {
      return menuLang(this.$lang, 'common_txt280', 'Telegram')
    },
    gameList() {
      const images = {
        luckyWheel: require('@/assets/img/otgame/LuckyWheel.png'),
        luckyDraw: require('@/assets/img/otgame/LuckyDraw.png')
      }
      const labels = {
        luckyWheel: { label: 'Daily Fortune', name: 'Lucky Wheel', theme: 'green' },
        luckyDraw: { label: 'Epic Rewards', name: 'Lucky Draw', theme: 'gold' }
      }
      return gameActivityEntries
        .filter((entry) => this.isNavigationEntryAvailable(entry))
        .map((entry) => ({
          ...entry,
          ...labels[entry.key],
          icon: images[entry.key]
        }))
    }
  },
  methods: {
    toggleCountryDrop() {
      this.showCountryDrop = !this.showCountryDrop
      if (this.showCountryDrop && this.countryList.length === 0) {
        this.fetchCountryList()
      }
    },
    async fetchCountryList() {
      this.countryListLoading = true
      this.countryListLoadFailed = false
      try {
        const data = await GetPlatformList()
        if (isOkResponse(data)) {
          this.countryList = normalizePlatformList(data)
          this.countryListLoadFailed = false
          return
        }
        this.countryList = []
        this.countryListLoadFailed = true
        if (typeof this.$toast === 'function') {
          this.$toast({ message: menuFailureMessage(data, MENU_COUNTRY_UNAVAILABLE_MESSAGE), icon: 'cross' })
        }
      } catch (e) {
        this.countryList = []
        this.countryListLoadFailed = true
        if (typeof this.$toast === 'function') {
          this.$toast({ message: menuFailureMessage(e, MENU_COUNTRY_UNAVAILABLE_MESSAGE), icon: 'cross' })
        }
      } finally {
        this.countryListLoading = false
      }
    },
    async getCsLinkResponse() {
      if (this.csLinkResponse) return this.csLinkResponse
      try {
        const data = await GetCsLink()
        this.csLinkResponse = data
        return data
      } catch (error) {
        if (error && error.status === 'need_login') return null
        return null
      }
    },
    async openMenuCustomerService(type) {
      try {
        const data = await this.getCsLinkResponse()
        if (data) {
          if (this.openConfiguredMenuLink(data, type)) return
          if (isMenuCustomerServiceType(type) && openCustomerServiceUrl(data, type)) return
          if (typeof this.$toast === 'function') {
            this.$toast({ message: menuFailureMessage(data, MENU_CUSTOMER_SERVICE_UNAVAILABLE_MESSAGE), icon: 'cross' })
          }
          return
        }
      } catch (error) {
        if (error && error.status === 'need_login') {
          this.$bus.$emit('openLogin')
          return
        }
        if (typeof this.$toast === 'function') {
          this.$toast({ message: menuFailureMessage(error, MENU_CUSTOMER_SERVICE_UNAVAILABLE_MESSAGE), icon: 'cross' })
        }
        return
      }
      if (typeof this.$toast === 'function') {
        this.$toast({ message: menuFailureMessage(null, MENU_CUSTOMER_SERVICE_UNAVAILABLE_MESSAGE), icon: 'cross' })
      }
      openCustomerServiceUrl(null, type)
    },
    openConfiguredMenuLink(response, type) {
      const url = resolveConfiguredMenuLink(response, type)
      if (!url) return false
      return openExternalUrl(url)
    },
    selectCountryMenu(item) {
      const platform = safeObject(item)
      if (!platform.country) return
      const storage = getLocalStorage()
      const prevPlatform = safeGetStorageItem(storage, 'platform')
      logoutIfReloginRequiredWithToken(prevPlatform, platform.platform)
      safeSetStorageItem(storage, 'currency', safeString(platform.currency))
      safeSetStorageItem(storage, 'areaCode', safeString(platform.areaCode))
      safeSetStorageItem(storage, 'currencyUnit', safeString(platform.currencyUnit))
      safeSetStorageItem(storage, 'platform', safeString(platform.platform))
      safeSetStorageItem(storage, 'defaultLanguage', safeString(platform.defaultLanguage))
      safeSetStorageItem(storage, 'iconUrl', safeString(platform.iconUrl))
      safeSetStorageItem(storage, 'countryName', safeString(platform.countryName))
      safeSetStorageItem(storage, 'country', safeString(platform.country))
      this.showCountryDrop = false
      this.$router.replace('/' + platform.country + '/home')
      setTimeout(() => { reloadLocationSafely() }, 200)
    },
    async gotoActive(i) {
      const storage = getLocalStorage()
      if (!safeGetStorageItem(storage, 'token')) {
        this.$bus.$emit('openLogin')
        return
      }
      if (i === 0) {
        this.$jumpTo('/rechargeCont')
        return
      }
      try {
        const data = await WithdrawInit()
        if (isNeedLogin(data)) {
          this.$bus.$emit('openLogin')
          return
        }
        if (!isOkResponse(data)) {
          if (typeof this.$toast === 'function') {
            this.$toast({ message: menuFailureMessage(data, MENU_WITHDRAW_UNAVAILABLE_MESSAGE), icon: 'cross' })
          }
          return
        }
        const route = getMineActionRoute('withdraw', {
          loggedIn: true,
          profile: responseContent(data),
          source: 'menu'
        })
        if (route.requiresLogin) {
          this.$bus.$emit('openLogin')
          return
        }
        this.$jumpTo(route.path, route.query || {})
      } catch (e) {
        if (typeof this.$toast === 'function') {
          this.$toast({ message: menuFailureMessage(e, MENU_WITHDRAW_UNAVAILABLE_MESSAGE), icon: 'cross' })
        }
      }
    },
    goProtectedActivity(path) {
      const storage = getLocalStorage()
      if (!safeGetStorageItem(storage, 'token')) {
        this.$bus.$emit('openLogin')
        return false
      }
      this.$jumpTo(path)
      return true
    },
    isNavigationEntryAvailable(entry) {
      return !!entry && entry.enabled !== false && isActivityVisible(entry, { defaultVisible: true })
    },
    showNavigationUnavailable(entry) {
      if (typeof this.$toast !== 'function') return
      this.$toast({
        message: (entry && (entry.unavailableMessage || entry.message || entry.reason)) ||
          this.$lang.bonus_unavailable_backend ||
          MENU_ENTRY_UNAVAILABLE_MESSAGE,
        icon: 'cross'
      })
    },
    openNavigationEntry(entry) {
      if (!entry || !entry.route) return false
      if (!this.isNavigationEntryAvailable(entry)) {
        this.showNavigationUnavailable(entry)
        return false
      }
      if (entry.requiresLogin) return this.goProtectedActivity(entry.route)
      this.$jumpTo(entry.route)
      return true
    },
    goToBBB(i) {
      this.openNavigationEntry(entryAt(this.gameList, i))
    },
    goToCCC(i) {
      this.openNavigationEntry(entryAt(this.mtList, i))
    },
    goToDDD(i) {
      if (i === 0) {
        return this.openMenuCustomerService('official')
      } else if (i === 1) {
        return this.openMenuCustomerService('telegram')
      }
    },
    goToSocial(i) {
      const item = entryAt(this.socialList, i)
      if (!item) return
      return this.openMenuCustomerService(item.serviceType)
    }
  }
}
</script>

<style lang="less" scoped>
@primary: @brand;        /* 强调/激活 → 火焰橙(原绿 #24ee89) */
@gold: #ffb020;          /* 钱/奖 → 金 = --vk-gold(原黄 #f8f510) */
@bg-dark: @vk-bg;        /* 页面底 */
@card-dark: @vk-surface; /* 卡片面 */

.menu-page {
  min-height: 100vh;
  background: @bg-dark;
  padding: 16px;
  padding-bottom: 80px;
}

// ====== PRIMARY ACTIONS ======
.menu-actions {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;

  &__btn {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: 64px;
    border-radius: 16px;
    border: none;
    cursor: pointer;
    font-size: var(--vk-fs-xs);
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.1em;

    &:active {
      transform: scale(0.95);
    }

    &--deposit {
      background: @brand-grad; /* 存款=火焰橙 CTA(对齐 mine) */
      color: #fff;
      box-shadow: 0 4px 15px fade(@brand, 30%);
    }

    &--withdraw {
      background: transparent; /* 提现=幽灵 */
      color: @vk-t2;
      border: 1px solid @vk-border;
    }
  }
}

// ====== REGION SELECTOR ======
.menu-region-wrap {
  position: relative;
  margin-bottom: 20px;
}
.menu-region {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: fade(@card-dark, 40%);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__label {
    font-size: var(--vk-fs-base);
    font-weight: 500;
    color: #cbd5e1;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #0F172A;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  &__flag {
    border-radius: 2px;
    object-fit: cover;
  }

  &__name {
    font-size: var(--vk-fs-xs);
    font-weight: 700;
    color: #f1f5f9;
  }
}
.menu-country-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  background: #252936;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  max-height: 220px;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  margin-top: 4px;

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    cursor: pointer;
    font-size: var(--vk-fs-sm);
    color: #ffffff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);

    &:last-child { border-bottom: none; }
    &:active { background: fade(@brand, 10%); }
  }

  &__code {
    margin-left: auto;
    color: @primary;
    font-weight: 600;
    font-size: var(--vk-fs-xs);
  }
}

// ====== SECTION ======
.menu-section {
  margin-bottom: 20px;

  &__title {
    font-size: var(--vk-fs-2xs);
    font-weight: 700;
    color: var(--vk-t3);
    text-transform: uppercase;
    letter-spacing: 0.2em;
    padding-left: 4px;
    margin-bottom: 12px;
  }
}

// ====== SOCIAL ======
.menu-social {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 12px;
  background: fade(@card-dark, 20%);
  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.05);

  &__item {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: @card-dark;
    border: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;

    &:active {
      border-color: fade(@primary, 50%);
      transform: scale(1.05);
    }
  }
}

// ====== GAME CARDS ======
.menu-games {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.menu-game-card {
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  height: 120px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;

  &--green {
    background: linear-gradient(135deg, #3a1505, #5a2410, @vk-surface); /* 火焰橙暖调(去靛紫) */
  }

  &--gold {
    background: linear-gradient(135deg, #3a2a05, #5a4410, @vk-surface); /* 金暖调(去蓝) */
  }

  &__top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__icon {
    width: 168px;
    height: 48px;
    border-radius: 12px;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;

    &--green {
      border: 1px solid fade(@primary, 20%);
      box-shadow: 0 0 15px fade(@primary, 20%);
    }

    &--gold {
      border: 1px solid fade(@gold, 20%);
      box-shadow: 0 0 15px fade(@gold, 20%);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__bottom {
    // at bottom
  }

  &__label {
    font-size: var(--vk-fs-2xs);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.2em;
    margin-bottom: 2px;
    color: @primary;

    .menu-game-card--gold & {
      color: @gold;
    }
  }

  &__name {
    font-size: var(--vk-fs-xl);
    font-weight: 700;
    color: #fff;
  }

  &:active {
    transform: scale(0.98);
  }
}

// ====== FUNCTION NAV LIST ======
.menu-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 20px;

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-radius: 12px;
    color: var(--vk-t2);
    cursor: pointer;
    transition: all 0.2s;

    &:active {
      background: fade(@card-dark, 40%);
      color: #f1f5f9;
    }

    &--highlight {
      background: fade(@primary, 5%);
      border: 1px solid fade(@primary, 10%);

      .menu-nav__left span {
        color: @primary;
        font-weight: 600;
      }
    }
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 16px;

    span {
      font-size: var(--vk-fs-base);
      font-weight: 500;
    }
  }

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: @primary;
  }
}

// ====== RESPONSIVE ======
@media (min-width: 769px) {
  .menu-page {
    max-width: var(--vk-pc-col);
    margin: 0 auto;
  }
}
</style>
